import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { getJSON } from '@/lib/api'
import type { Person } from '@/lib/contacts/types'
import type { PipelineAction } from '@/pages/RenewalPipelinePage.types'
import { EndMembershipDialog } from '@/pages/PersonMembershipDialogs'
import {
  RecordPaymentDialog,
  submitPaymentForm,
  submitPhonePaymentForm,
} from '@/pages/RecordPaymentDialog'
import type { InvoiceResponse, PaymentForm, PhonePaymentForm } from '@/pages/InvoicesPage.types'

export function PipelineActionDialog({
  action,
  currentAssociationDate,
  onClose,
  onResolved,
}: {
  action: PipelineAction | null
  currentAssociationDate?: string
  onClose: () => void
  onResolved?: () => void | Promise<void>
}) {
  const queryClient = useQueryClient()
  const invoiceId = action?.kind === 'record-payment' ? action.row.invoice_id : null
  const personId = action?.kind === 'suspend' ? action.row.person_id : null
  const invoiceQuery = useQuery({
    queryKey: ['invoice', invoiceId],
    queryFn: () => getJSON<InvoiceResponse>(`/api/v1/invoices/${invoiceId}`),
    enabled: invoiceId !== null,
    retry: false,
  })
  const personQuery = useQuery<{ person: Person }>({
    queryKey: ['person', personId],
    queryFn: () => getJSON<{ person: Person }>(`/api/v1/persons/${personId}`),
    enabled: personId !== null,
    retry: false,
  })
  const payment = useMutation({
    mutationFn: (form: PaymentForm) =>
      submitPaymentForm(invoiceQuery.data!.invoice.contact_id, form),
    onSuccess: () => finishAction(queryClient, onClose, onResolved),
  })
  const phonePayment = useMutation({
    mutationFn: (form: PhonePaymentForm) => submitPhonePaymentForm(invoiceId as number, form),
    onSuccess: () => finishAction(queryClient, onClose, onResolved),
  })

  if (action?.kind === 'record-payment' && invoiceQuery.data?.invoice) {
    return (
      <RecordPaymentDialog
        invoice={invoiceQuery.data.invoice}
        currentAssociationDate={currentAssociationDate}
        pending={payment.isPending}
        error={payment.error}
        phonePending={phonePayment.isPending}
        phoneError={phonePayment.error}
        onClose={onClose}
        onSubmit={(form) => payment.mutate(form)}
        onSubmitPhone={(form) => phonePayment.mutate(form)}
      />
    )
  }

  if (action?.kind === 'suspend' && personQuery.data?.person) {
    return (
      <EndMembershipDialog
        action="suspend"
        person={personQuery.data.person}
        onClose={onClose}
        onChanged={() => finishAction(queryClient, onClose, onResolved)}
      />
    )
  }

  return null
}

async function finishAction(
  queryClient: QueryClient,
  onClose: () => void,
  onResolved?: () => void | Promise<void>,
) {
  await onResolved?.()
  onClose()
  await queryClient.invalidateQueries({ queryKey: ['renewal-pipeline'] })
}
